import { Money } from './money'
import { CommissionsData, Condition } from './orderData'
import {
  OrderAsset,
  OrderCancellationReason,
  OrderTransactionChildStatusEnum,
  OrderTransactionStatusEnum,
} from './orderTransaction'
import { OrderTypeEnum } from './orderType'

export enum TransactionType {
  withdrawal = 'WITHDRAWAL',
  deposit = 'DEPOSIT',
  sell = 'SELL',
  buy = 'BUY',
  dividendCash = 'DIVIDEND_CASH',
  dividendShares = 'DIVIDEND_SHARES',
  walletTransferIncoming = 'WALLET_TRANSFER_INCOMING',
  walletTransferOutging = 'WALLET_TRANSFER_OUTGOING',
  subscription = 'SUBSCRIPTION',
  transfer = 'TRANSFER',
  redemption = 'REDEMPTION',
  noteCredit = 'NOTE_CREDIT',
  noteDebit = 'NOTE_DEBIT',
  safekeeping = 'SAFEKEEPING',
}
export interface ForexRate {
  from: string
  to: string
  value: number
}

export enum NotCancellableReason {
  marketOrder = 'MARKET_ORDER',
  marketClosed = 'MARKET_CLOSED',
  orderFinalized = 'ORDER_FINALIZED',
  orderProcessed = 'ORDER_PROCESSED',
}

export interface Transaction {
  id: string
  title: string
  type: TransactionType
  logo: string
  date: string
  amount: Money
  ticker: string
  orderDetails: {
    condition?: Condition
    type: TransactionType
    status: OrderTransactionStatusEnum
    statusText: string
    ticker: string
    childStatus: OrderTransactionChildStatusEnum
    childStatusText: string
    method: OrderTypeEnum
    shares: {
      executed: number
      requested: number
    }
    sharePrice: Money
    isCancellable?: boolean
    notCancellableReason?: NotCancellableReason
    lockedCash?: Money
    reason?: OrderCancellationReason
    reasonText?: string
    units?: number
    nav?: Money
    // target asset is only for funds assets when it is a transfer operation
    targetAsset?: {
      id: string
      name: string
      isin: string
      ticker: string
      market: string
      currency: string
      logoUrl: string
      units: number
    }
  }
  dividendSharesDetails?: {
    ticker: string
    stockType: string
    financialEvent: string
    shares: number
    forex?: ForexRate
  }
  asset?: OrderAsset
}

export interface TransactionDetail {
  title: string
  ticker?: string
  type: TransactionType
  logo: string
  date: string
  amount: Money
  status: OrderTransactionStatusEnum
  documentId: string
  forex?: ForexRate
}

export interface BuySellTransaction extends TransactionDetail {
  id: string
  shares?: {
    executed: number
    requested: number
  }
  units?: number
  asset: OrderAsset
  taxes: Money
  fees?: Money
  totalCost?: Money
  netAmount?: Money
  netAmountInPortfoliosCurrency?: Money
  exchangeRateAverage?: number
  orderMethod?: OrderTypeEnum
  childStatus?: OrderTransactionChildStatusEnum
  childStatusText?: string
  reason?: OrderCancellationReason
  reasonText?: string
  nav?: Money
  creationDate?: string
  estimatedPrice?: Money
  executedPrice?: Money
  expirationDate?: string
  grossAmount?: Money
  lockedCash?: Money
  assetCurrency?: string
  statusText?: string
  contingenciesBuffer?: number
  contingenciesBufferAmount?: Money
  requiredNetAmount?: Money
  isCancellable?: boolean
  notCancellableReason?: NotCancellableReason
  commissions?: CommissionsData
  cutOffTime?: string
  type: TransactionType
  transferTargetAsset?: {
    id: string
    ticker: string
    isin: string
    currency: string
    name: string
    logoUrl: string
    units: number
    nav: {
      amount: number
      currency: string
    }
  }
  completedDate?: string
  cancellationDate?: string
  condition?: Condition
}

export interface DepositWithdrawalTransaction extends TransactionDetail {
  fees: Money
  account: string
  type: TransactionType.deposit | TransactionType.withdrawal
}

export interface DividendTransaction extends TransactionDetail {
  type: TransactionType.dividendCash | TransactionType.dividendShares
  sharesQuantity: number
}

export interface NoteTransaction extends TransactionDetail {
  id: string
  type: TransactionType.noteCredit | TransactionType.noteDebit
  isCancellable: boolean
  taxes: Money
  total: Money
  concept: string
}

export interface FundTransactionDetail {
  id: string
  status: OrderTransactionStatusEnum
  statusText: string
  childStatus: OrderTransactionChildStatusEnum
  childStatusText: string
  units: number
  method: OrderTypeEnum
  type:
    | TransactionType.subscription
    | TransactionType.redemption
    | TransactionType.transfer
  nav: Money
  creationDate: string
  completedDate: string
  documentId: string
  exchangeRateAverage: number
  asset: {
    id: string
    ticker: string
    isin: string
    currency: string
    name: string
    logoUrl: string
  }
  // Only applies when is a transfer operation
  targetAsset?: {
    id: string
    name: string
    isin: string
    ticker: string
    market: string
    currency: string
    logoUrl: string
    units: number
    nav: {
      amount: number
      currency: string
    }
  }
  calculations: {
    grossAmount: Money
    netAmount: Money
    netAmountInPortfolioCurrency: Money
    totalCost: Money
  }
  commissions: {
    totalCommission: Money
  }
  isCancellable: false
  notCancellableReason: NotCancellableReason
  cutOffTime: string
}
export interface SafekeepingTransaction extends TransactionDetail {
  id: string
  fee?: number
  type: TransactionType.safekeeping
}
export interface WalletTransaction extends TransactionDetail {
  id: string
  forex?: ForexRate
  fees?: Money
}

export type TransactionDetailType =
  | BuySellTransaction
  | DepositWithdrawalTransaction
  | DividendTransaction
  | WalletTransaction
  | FundTransactionDetail
  | NoteTransaction
  | SafekeepingTransaction
